// A control's 0…1 position ↔ a target's value: the binding's range, invert and curve, then the param's own curve, so a binding's min/max are slider positions.
import { quantize } from '~/lib/params'
import { positionOf, valueAt } from '~/lib/params/curve'

const clamp01 = x => (x < 0 ? 0 : x > 1 ? 1 : x)

const isNumeric = s => s?.type === 'number' || s?.type === 'integer'

export const isMappable = schema => {
	if (!schema || schema.readOnly) return false
	if (Array.isArray(schema.enum)) return schema.enum.length > 0
	if (schema.type === 'boolean') return true
	return isNumeric(schema) && Number.isFinite(schema.minimum) && Number.isFinite(schema.maximum)
}

// `exp` gives a fader more travel at the bottom of the range, `log` at the top.
const CURVE = {
	linear: [x => x, x => x],
	exp: [x => x * x, x => Math.sqrt(x)],
	log: [x => Math.sqrt(x), x => x * x],
}

const curveOf = t => CURVE[t?.curve] ?? CURVE.linear

/** A numeric value on the schema's step grid, then through the write rule's own precision and wrap. */
export const snapToStep = (schema, v) => {
	const step = schema.step ?? 1
	const lo = schema.minimum
	const snapped = step > 0 ? lo + Math.round((v - lo) / step) * step : v
	return quantize(schema, Math.min(schema.maximum, Math.max(lo, snapped)))
}

/**
 * The target value at control position `hw` (0…1).
 *
 * @param t the binding target: `{ min, max, invert, curve }`
 */
export const toDomain = (hw, schema, t = {}) => {
	let x = clamp01(hw)
	if (t.invert) x = 1 - x
	x = curveOf(t)[0](x)
	const n = clamp01((t.min ?? 0) + ((t.max ?? 1) - (t.min ?? 0)) * x)
	if (Array.isArray(schema.enum))
		return schema.enum[Math.min(schema.enum.length - 1, Math.floor(n * schema.enum.length))]
	if (schema.type === 'boolean') return n >= 0.5
	return snapToStep(schema, valueAt(schema, n))
}

/** Where `value` sits as a control position (0…1) — the inverse of `toDomain`, clamped when the value is outside the binding's range. */
export const fromDomain = (value, schema, t = {}) => {
	let n
	if (Array.isArray(schema.enum)) {
		const i = schema.enum.indexOf(value)
		n = i < 0 ? 0 : (i + 0.5) / schema.enum.length
	} else if (schema.type === 'boolean') n = value ? 1 : 0
	else n = positionOf(schema, value)
	const lo = t.min ?? 0
	const hi = t.max ?? 1
	let x = hi === lo ? 0 : clamp01((n - lo) / (hi - lo))
	x = curveOf(t)[1](x)
	if (t.invert) x = 1 - x
	return Number.isFinite(x) ? x : 0
}

/**
 * How far one relative tick moves the control position: one schema step, or 1/256 of the travel when steps are finer — so an integer 0…10 moves a whole unit per detent and `x` over ±1000 does not need 2000 of them.
 */
export const relativeTick = (schema, t = {}) => {
	const range = Math.abs((t.max ?? 1) - (t.min ?? 0)) || 1
	let stepNorm
	if (Array.isArray(schema.enum)) stepNorm = 1 / schema.enum.length
	else if (schema.type === 'boolean') stepNorm = 1
	else stepNorm = (schema.step ?? 1) / (schema.maximum - schema.minimum || 1)
	return Math.max(1 / 256, stepNorm / range)
}

export { clamp01 }
